import {
  For,
  Index,
  Show,
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  onMount,
  untrack,
} from "solid-js";
import { Portal } from "solid-js/web";
import { useBeforeLeave } from "@solidjs/router";
import type { CatalogExercise, Exercise, ExerciseHistoryItem } from "../api/types";
import { t } from "../i18n";
import { lastTimeHint } from "../lib/activity";
import {
  type ActiveExercise,
  activeType,
  applyCatalogToActive,
  exercisesMissingValues,
} from "../lib/activeWorkout";
import {
  REPS_STEP,
  WEIGHT_STEP,
  appendSet,
  duplicateSet,
  isDraftDirty,
  nudgeReps,
  nudgeWeight,
  patchSetById,
  removeSetById,
} from "../lib/exerciseDraft";
import { pushBackInterceptor } from "../lib/nav";
import { tgConfirm, tgHapticImpact, tgHapticNotification } from "../lib/telegram";
import { createViewportBox } from "../lib/viewport";
import ActivityFields from "./ActivityFields";
import DecimalField from "./DecimalField";
import ExerciseIcon from "./ExerciseIcon";
import ExercisePicker from "./ExercisePicker";

const VALUE_KEYS = ["durationSec", "distanceM", "intensity", "rounds", "invalid"] as const;

function valuesOnly(patch: Partial<Exercise>): Partial<ActiveExercise> {
  const out: Record<string, unknown> = {};
  for (const k of VALUE_KEYS) if (k in patch) out[k] = patch[k];
  return out as Partial<ActiveExercise>;
}

/** Whole-number field: numeric keypad, select-all on focus, restores on empty blur. */
function IntField(props: { value: number; label: string; onChange: (n: number) => void }) {
  const [focused, setFocused] = createSignal(false);
  const [draft, setDraft] = createSignal("");
  return (
    <input
      class="afs-num"
      type="text"
      inputmode="numeric"
      pattern="[0-9]*"
      enterkeyhint="done"
      autocomplete="off"
      aria-label={props.label}
      value={focused() ? draft() : String(props.value)}
      onFocus={(e) => {
        setDraft(String(props.value));
        setFocused(true);
        const el = e.currentTarget;
        queueMicrotask(() => el.select());
      }}
      onInput={(e) => {
        const digits = e.currentTarget.value.replace(/\D/g, "").slice(0, 4);
        if (digits !== e.currentTarget.value) e.currentTarget.value = digits;
        setDraft(digits);
        const n = Number(digits);
        if (digits && n >= 1) props.onChange(n);
      }}
      onBlur={() => setFocused(false)}
    />
  );
}

const ChevronLeft = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <path d="M15 5l-7 7 7 7" />
  </svg>
);
const CopyIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <rect x="9" y="9" width="11" height="11" rx="2.5" />
    <path d="M5 15V6.5A2.5 2.5 0 0 1 7.5 4H15" />
  </svg>
);
const CloseIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

interface Props {
  /** Exercise as opened (a fresh blank one when adding). Snapshotted on mount. */
  initial: ActiveExercise;
  isNew: boolean;
  history: (name: string) => ExerciseHistoryItem[];
  onNeedHistory: (name: string) => void;
  onSave: (ex: ActiveExercise) => void;
  onDelete?: (id: string) => void;
  /** Leave the editor (the parent navigates; unsaved edits are confirmed here). */
  onClose: () => void;
}

/**
 * Full-screen exercise editor for the live workout: sticky header, search-first picker,
 * big fields and a primary action that stays above the soft keyboard.
 * Works on a draft; nothing touches the session until Save.
 */
export default function ExerciseFullScreen(props: Props) {
  const initial = untrack(() => props.initial);
  const [draft, setDraft] = createSignal<ActiveExercise>(initial);
  const [picking, setPicking] = createSignal(untrack(() => props.isNew) || !initial.name.trim());
  const [query, setQuery] = createSignal("");
  const [showErrors, setShowErrors] = createSignal(false);
  const [notesOpen, setNotesOpen] = createSignal(Boolean(initial.notes));
  const box = createViewportBox();
  let sheetOpen = false;
  let leaving = false;
  let bodyEl: HTMLDivElement | undefined;

  const dirty = createMemo(() => isDraftDirty(initial, draft()));
  const type = () => activeType(draft());
  const named = () => draft().name.trim().length > 0;
  const hist = () => props.history(draft().name);

  createEffect(() => {
    const n = draft().name.trim();
    if (n) props.onNeedHistory(n);
  });

  /** Back button, Esc, header arrow: step out of the picker, else confirm unsaved edits, then leave. */
  async function requestClose() {
    if (leaving) return;
    if (picking() && named() && !props.isNew) {
      setPicking(false);
      return;
    }
    if (dirty() && !(await tgConfirm(t("DISCARD_EDITS_CONFIRM")))) return;
    leaving = true;
    props.onClose();
  }

  // Browser back / swipe-back (history pops straight past requestClose): same rules.
  useBeforeLeave((e) => {
    if (leaving) return;
    if (picking() && named() && !props.isNew) {
      e.preventDefault();
      setPicking(false);
      return;
    }
    if (!dirty()) return;
    e.preventDefault();
    void tgConfirm(t("DISCARD_EDITS_CONFIRM")).then((ok) => {
      if (!ok) return;
      leaving = true;
      e.retry(true);
    });
  });

  onMount(() => {
    const unBack = pushBackInterceptor(() => void requestClose());
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !sheetOpen) {
        e.preventDefault();
        void requestClose();
      }
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    onCleanup(() => {
      unBack();
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    });
    requestAnimationFrame(() => {
      if (picking()) bodyEl?.querySelector<HTMLInputElement>("[data-autofocus]")?.focus();
    });
  });

  // Keep the focused field visible when the keyboard opens / the viewport shrinks.
  const reveal = (el: Element | null) => {
    if (!el || !bodyEl?.contains(el)) return;
    if (!/^(INPUT|TEXTAREA)$/.test(el.tagName)) return;
    el.scrollIntoView({ block: "center", behavior: "smooth" });
  };
  createEffect(() => {
    box().height;
    const id = setTimeout(() => reveal(document.activeElement), 80);
    onCleanup(() => clearTimeout(id));
  });

  function patch(p: Partial<ActiveExercise>) {
    setDraft((d) => ({ ...d, ...p }));
  }

  function pickCatalog(item: CatalogExercise) {
    setDraft((d) => applyCatalogToActive(d, item));
    props.onNeedHistory(item.name);
    setQuery("");
    setPicking(false);
    tgHapticImpact("light");
  }

  function save() {
    const d = draft();
    if (!d.name.trim()) {
      setPicking(true);
      return;
    }
    if (exercisesMissingValues([d]).length) {
      setShowErrors(true);
      tgHapticNotification("error");
      queueMicrotask(() =>
        bodyEl
          ?.querySelector(".is-invalid, .field-hint.is-error")
          ?.scrollIntoView({ block: "center", behavior: "smooth" }),
      );
      return;
    }
    leaving = true;
    props.onSave({ ...d, name: d.name.trim() });
  }

  async function remove() {
    if (!props.onDelete) return;
    if (!(await tgConfirm(t("CONFIRM_DELETE")))) return;
    leaving = true;
    props.onDelete(draft().id);
  }

  const title = () =>
    picking()
      ? props.isNew
        ? t("ADD_EXERCISE")
        : t("FIND_EXERCISE")
      : draft().name.trim() || t("ADD_EXERCISE");

  const unit = (u: string) => (u === "kg" ? t("KG") : u);

  return (
    <Portal>
      <div
        class="afs"
        role="dialog"
        aria-modal="true"
        aria-label={title()}
        style={{ top: `${box().top}px`, height: `${box().height}px` }}
        onFocusIn={(e) => {
          const el = e.target as Element;
          setTimeout(() => reveal(el), 260);
        }}
      >
        <div class="afs-inner">
          <header class="afs-header">
            <button
              type="button"
              class="afs-back"
              aria-label={t("BACK")}
              onClick={() => void requestClose()}
            >
              <ChevronLeft />
            </button>
            <h2 class="afs-title">{title()}</h2>
            <Show when={!picking()}>
              <button type="button" class="afs-save-link" onClick={save}>
                {props.isNew ? t("ADD_SHORT") : t("SAVE")}
              </button>
            </Show>
          </header>

          <div class={`afs-body${picking() ? " is-picking" : ""}`} ref={bodyEl}>
            <Show
              when={!picking()}
              fallback={
                <ExercisePicker
                  query={query()}
                  onQuery={setQuery}
                  onPick={pickCatalog}
                  onSheetOpen={(o) => (sheetOpen = o)}
                />
              }
            >
              <button type="button" class="afs-name" onClick={() => setPicking(true)}>
                <ExerciseIcon icon={draft().icon} type={type()} class="afs-name-icon" />
                <span class="afs-name-text">{draft().name}</span>
                <span class="afs-name-change">{t("CHANGE")}</span>
              </button>

              <Show when={type() !== "strength" && lastTimeHint(hist(), type())}>
                {(hint) => <p class="ex-last-time afs-hint">{hint()}</p>}
              </Show>
              <Show when={type() === "strength" && hist().length > 0}>
                <div class="afs-hist">
                  <For each={hist().slice(0, 3)}>
                    {(h) => (
                      <span class="aw-focus-history-item tabular-nums">
                        {h.sets}×{h.reps}
                        {h.weight != null ? ` · ${h.weight}` : ""}
                      </span>
                    )}
                  </For>
                </div>
              </Show>

              <Show
                when={type() === "strength"}
                fallback={
                  <div class="afs-fields space-y-3">
                    <ActivityFields
                      ex={{
                        name: draft().name,
                        sets: 1,
                        reps: 1,
                        weight: null,
                        exerciseType: type(),
                        durationSec: draft().durationSec,
                        distanceM: draft().distanceM,
                        intensity: draft().intensity,
                        rounds: draft().rounds,
                        invalid: draft().invalid,
                      }}
                      showErrors={showErrors()}
                      onPatch={(p) => patch(valuesOnly(p))}
                    />
                  </div>
                }
              >
                <div class="afs-sets">
                  <Index each={draft().sets}>
                    {(st, i) => (
                      <div class={`afs-set${st().done ? " is-done" : ""}`}>
                        <div class="afs-set-top">
                          <span class="afs-set-num">
                            {t("SET_N").replace("{n}", String(i + 1))}
                          </span>
                          <button
                            type="button"
                            class="afs-icon-btn"
                            aria-label={t("DUPLICATE_SET")}
                            title={t("DUPLICATE_SET")}
                            onClick={() => setDraft((d) => duplicateSet(d, st().id))}
                          >
                            <CopyIcon />
                          </button>
                          <button
                            type="button"
                            class="afs-icon-btn"
                            aria-label={t("REMOVE_SET")}
                            title={t("REMOVE_SET")}
                            disabled={draft().sets.length <= 1}
                            onClick={() => setDraft((d) => removeSetById(d, st().id))}
                          >
                            <CloseIcon />
                          </button>
                        </div>
                        <div class="afs-set-fields">
                          <div class="afs-stepper">
                            <button
                              type="button"
                              aria-label="−"
                              onClick={() =>
                                setDraft((d) =>
                                  patchSetById(d, st().id, {
                                    weight: nudgeWeight(st().weight, -WEIGHT_STEP),
                                  }),
                                )
                              }
                            >
                              −
                            </button>
                            <div class="afs-stepper-val">
                              <DecimalField
                                class="afs-num"
                                value={st().weight}
                                min={0}
                                decimals={2}
                                placeholder="—"
                                selectOnFocus
                                aria-label={`${t("WEIGHT")}, ${t("KG")}`}
                                onChange={(weight) =>
                                  setDraft((d) => patchSetById(d, st().id, { weight }))
                                }
                              />
                              <span class="afs-unit">{unit(st().unit)}</span>
                            </div>
                            <button
                              type="button"
                              aria-label="+"
                              onClick={() =>
                                setDraft((d) =>
                                  patchSetById(d, st().id, {
                                    weight: nudgeWeight(st().weight, WEIGHT_STEP),
                                  }),
                                )
                              }
                            >
                              +
                            </button>
                          </div>
                          <div class="afs-stepper">
                            <button
                              type="button"
                              aria-label="−"
                              onClick={() =>
                                setDraft((d) =>
                                  patchSetById(d, st().id, {
                                    reps: nudgeReps(st().reps, -REPS_STEP),
                                  }),
                                )
                              }
                            >
                              −
                            </button>
                            <div class="afs-stepper-val">
                              <IntField
                                value={st().reps}
                                label={t("REPS")}
                                onChange={(reps) =>
                                  setDraft((d) => patchSetById(d, st().id, { reps }))
                                }
                              />
                              <span class="afs-unit">{t("REPS_UNIT")}</span>
                            </div>
                            <button
                              type="button"
                              aria-label="+"
                              onClick={() =>
                                setDraft((d) =>
                                  patchSetById(d, st().id, {
                                    reps: nudgeReps(st().reps, REPS_STEP),
                                  }),
                                )
                              }
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </Index>
                </div>
              </Show>

              <div class="afs-notes">
                <button
                  type="button"
                  class="afs-notes-toggle"
                  aria-expanded={notesOpen()}
                  onClick={() => setNotesOpen((v) => !v)}
                >
                  {t("NOTES")} {notesOpen() ? "▾" : "▸"}
                </button>
                <Show when={notesOpen()}>
                  <textarea
                    class="input"
                    placeholder={t("EXERCISE_NOTES_PLACEHOLDER")}
                    value={draft().notes}
                    onInput={(e) => patch({ notes: e.currentTarget.value })}
                  />
                </Show>
              </div>

              <Show when={!props.isNew && props.onDelete}>
                <button type="button" class="afs-delete" onClick={() => void remove()}>
                  {t("DELETE_EXERCISE")}
                </button>
              </Show>
            </Show>
          </div>

          <Show when={!picking()}>
            <footer class={`afs-footer${box().keyboard ? " is-kb" : ""}`}>
              <Show when={type() === "strength"}>
                <button
                  type="button"
                  class="btn-secondary afs-add-set"
                  onClick={() => {
                    setDraft((d) => appendSet(d));
                    queueMicrotask(() =>
                      bodyEl?.querySelector(".afs-set:last-child")?.scrollIntoView({ block: "nearest", behavior: "smooth" }),
                    );
                  }}
                >
                  + {t("ADD_SET")}
                </button>
              </Show>
              <button type="button" class="btn-primary afs-primary" onClick={save}>
                {props.isNew ? t("ADD_SHORT") : t("SAVE")}
              </button>
            </footer>
          </Show>
        </div>
      </div>
    </Portal>
  );
}
